/**
 * Clients page
 * Main page for managing clients with detail sidebar
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Icon, ClientModal } from "@/components/ui";
import { usersService } from "@/services/usersService";
import { citiesService } from "@/services/citiesService";
import { workOrdersService } from "@/services/workOrdersService";
import type {
  APIUser,
  APIUserRequest,
  APIUserDocument,
  APICity,
} from "@/types";
import styles from "./ClientsPage.module.css";

interface Client {
  id: string;
  nif: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  status: "active" | "inactive";
  sector?: string;
  provinceId: string;
  lastService?: string;
  password?: string;
}

interface Service {
  id: string;
  date: string;
  title: string;
  description: string;
  status: "completed" | "scheduled";
}

interface Document {
  id: string;
  /** ID numérico para llamadas a la API */
  numericId: number;
  name: string;
  date: string;
  size: string;
  type: "contrato" | "certificado" | "inspeccion";
  /** URL del fichero para descarga */
  url?: string;
}

const PAGE_SIZE = 20;
const HISTORY_MODAL_BATCH_SIZE = 30;

const VALID_DOC_TYPES = [
  "contrato",
  "certificado",
  "inspeccion",
] as const satisfies Document["type"][];

const DOCUMENT_TYPE_MAP: Record<string, Document["type"]> = {
  contrato: "contrato",
  contratos: "contrato",
  certificado: "certificado",
  certificados: "certificado",
  inspeccion: "inspeccion",
  inspecciones: "inspeccion",
};

/** Mapea APIUserDocument al tipo local Document */
const mapAPIDocument = (d: APIUserDocument): Document => {
  // Extrae el nombre del archivo evitando query-params de URLs firmadas (S3, Azure)
  const rawBaseName = (() => {
    try {
      return new URL(d.file).pathname.split("/").pop() ?? "";
    } catch {
      return d.file.split("/").pop() ?? "";
    }
  })();
  const fileName =
    d.name ?? (rawBaseName ? decodeURIComponent(rawBaseName) : "Documento");

  const sizeBytes = d.file_size ?? 0;
  const sizeStr =
    sizeBytes > 0
      ? sizeBytes >= 1024 * 1024
        ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.round(sizeBytes / 1024)} KB`
      : "";

  const date = d.created_at
    ? new Date(d.created_at).toLocaleDateString("es-ES", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  // Soporta shape backend con `type` y variantes legacy con `document_type`
  const rawType = (
    d.type ?? (d as APIUserDocument & { document_type?: string }).document_type
  )
    ?.toString()
    .trim()
    .toLowerCase();
  const normalizedType = rawType ? DOCUMENT_TYPE_MAP[rawType] : undefined;

  // Valida explícitamente que el tipo sea uno de los conocidos
  const docType: Document["type"] = normalizedType
    ? normalizedType
    : (VALID_DOC_TYPES as readonly string[]).includes(d.type)
      ? (d.type as Document["type"])
      : "contrato";

  return {
    id: String(d.id),
    numericId: d.id,
    name: fileName,
    date: date ? `Subido • ${date}` : "",
    size: sizeStr,
    type: docType,
    url: d.file || undefined,
  };
};

const mapAPIUserToClient = (u: APIUser): Client => ({
  id: String(u.id),
  nif: u.cif ?? "",
  name: u.company ?? [u.first_name, u.last_name].filter(Boolean).join(" "),
  contactPerson: [u.first_name, u.last_name].filter(Boolean).join(" "),
  email: u.email,
  phone: u.phone_number ?? "",
  address: u.address ?? "",
  city: u.city !== undefined && u.city !== null ? String(u.city) : "",
  province:
    u.province !== undefined && u.province !== null ? String(u.province) : "",
  postalCode: u.cp ?? "",
  status: u.is_active ? "active" : "inactive",
  sector: u.sector ?? undefined,
  provinceId:
    u.province !== undefined && u.province !== null ? String(u.province) : "",
});

const mapClientToAPIUserRequest = (
  client: Partial<Client>,
): Partial<APIUserRequest> => {
  const parts = (client.contactPerson ?? "").trim().split(/\s+/);
  return {
    first_name: parts[0] ?? "",
    last_name: parts.slice(1).join(" "),
    email: client.email ?? "",
    is_active: client.status !== "inactive",
    address: client.address ?? null,
    cif: client.nif ?? null,
    cp: client.postalCode ?? null,
    company: client.name ?? null,
    phone_number: client.phone || undefined,
    province: client.province ? Number(client.province) : null,
    /** city llega del select del modal como ID numérico en string */
    city: client.city ? Number(client.city) : null,
    password: client.password || undefined,
  };
};

const getAvatarColor = (name: string): string => {
  const colors = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const getInitials = (name: string): string => {
  const words = name.split(" ");
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Clients page component
 */
export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [stateFilter, setStateFilter] = useState("Todos");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeDocTab, setActiveDocTab] = useState("contrato");
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [allCities, setAllCities] = useState<APICity[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDeletingDocId, setIsDeletingDocId] = useState<number | null>(null);
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [uploadDocType, setUploadDocType] =
    useState<Document["type"]>("contrato");
  const [uploadFileName, setUploadFileName] = useState("");
  const [serviceHistory, setServiceHistory] = useState<Service[]>([]);
  const [isLoadingServiceHistory, setIsLoadingServiceHistory] = useState(false);
  const [showServiceHistoryModal, setShowServiceHistoryModal] = useState(false);
  const [historyModalVisibleCount, setHistoryModalVisibleCount] = useState(
    HISTORY_MODAL_BATCH_SIZE,
  );
  const uploadInputRef = useRef<HTMLInputElement>(null);
  /** Ref para cancelar actualizaciones de estado de peticiones de docs obsoletas */
  const loadDocsRequestId = useRef(0);
  const loadServicesRequestId = useRef(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const loadClients = async (targetPage: number) => {
    try {
      setIsLoading(true);
      const res = await usersService.list({
        role: 2,
        page: targetPage,
        page_size: PAGE_SIZE,
      });
      const mapped = res.results.map(mapAPIUserToClient);
      setClients(mapped);
      setTotalCount(res.count);
      setSelectedClient((prev) => {
        if (prev) {
          const updated = mapped.find((c) => c.id === prev.id);
          return updated ?? prev;
        }
        return mapped[0] ?? null;
      });
    } catch (err) {
      console.error("Error loading clients:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadClients(page);
  }, [page]);

  useEffect(() => {
    citiesService
      .list({ page_size: 500 })
      .then((res) => setAllCities(res.results))
      .catch((err) => console.error("Error loading cities:", err));
  }, []);

  const provinces = useMemo(
    () =>
      Array.from(
        new Map(allCities.map((c) => [c.province, c.province_name])).entries(),
      ).map(([id, name]) => ({ id, name })),
    [allCities],
  );

  /** Carga los documentos del cliente. Usa un contador para descartar respuestas obsoletas
   * en caso de cambio de cliente antes de que termine la petición anterior. */
  const loadDocuments = async (clientId: string) => {
    const requestId = ++loadDocsRequestId.current;
    setIsLoadingDocs(true);
    try {
      const docs = await usersService.listDocuments(Number(clientId));
      if (loadDocsRequestId.current !== requestId) return; // respuesta obsoleta
      setDocuments(docs.map(mapAPIDocument));
    } catch (err) {
      if (loadDocsRequestId.current !== requestId) return;
      console.error("Error loading documents:", err);
      setDocuments([]);
    } finally {
      if (loadDocsRequestId.current === requestId) {
        setIsLoadingDocs(false);
      }
    }
  };

  const loadServiceHistory = async (clientId: string) => {
    const requestId = ++loadServicesRequestId.current;
    setIsLoadingServiceHistory(true);

    try {
      const response = await workOrdersService.list({
        page: 1,
        page_size: 100,
        client: Number(clientId),
      });
      if (loadServicesRequestId.current !== requestId) return;

      const mappedServices: Service[] = [...response.results]
        .sort((a, b) => {
          const aTime = a.scheduled_date
            ? new Date(a.scheduled_date).getTime()
            : 0;
          const bTime = b.scheduled_date
            ? new Date(b.scheduled_date).getTime()
            : 0;
          return bTime - aTime;
        })
        .map((order) => {
          const serviceDate = order.scheduled_date
            ? new Date(order.scheduled_date).toLocaleDateString("es-ES", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "Sin fecha";
          const serviceTitle = order.plague?.name
            ? `Servicio de ${order.plague.name}`
            : "Servicio de control";

          return {
            id: order.order_number ?? String(order.id),
            date: serviceDate,
            title: serviceTitle,
            description: order.order_number ?? `Orden #${order.id}`,
            status: order.status === "completed" ? "completed" : "scheduled",
          };
        });

      setServiceHistory(mappedServices);
    } catch (err) {
      if (loadServicesRequestId.current !== requestId) return;
      console.error("Error loading service history:", err);
      setServiceHistory([]);
    } finally {
      if (loadServicesRequestId.current === requestId) {
        setIsLoadingServiceHistory(false);
      }
    }
  };

  // Recargar documentos cuando cambia el cliente seleccionado
  useEffect(() => {
    if (selectedClient) {
      void loadDocuments(selectedClient.id);
      void loadServiceHistory(selectedClient.id);
    } else {
      setDocuments([]);
      setServiceHistory([]);
    }
    setShowServiceHistoryModal(false);
    setShowUploadPanel(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.id]);

  const latestServices = useMemo(
    () => serviceHistory.slice(0, 4),
    [serviceHistory],
  );
  const visibleServiceHistoryInModal = useMemo(
    () => serviceHistory.slice(0, historyModalVisibleCount),
    [serviceHistory, historyModalVisibleCount],
  );

  const openServiceHistoryModal = () => {
    setHistoryModalVisibleCount(HISTORY_MODAL_BATCH_SIZE);
    setShowServiceHistoryModal(true);
  };

  const handleServiceHistoryModalScroll = (
    event: React.UIEvent<HTMLDivElement>,
  ) => {
    const target = event.currentTarget;
    const distanceToBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (
      distanceToBottom < 80 &&
      historyModalVisibleCount < serviceHistory.length
    ) {
      setHistoryModalVisibleCount((count) =>
        Math.min(count + HISTORY_MODAL_BATCH_SIZE, serviceHistory.length),
      );
    }
  };

  const filteredClients = useMemo(
    () =>
      clients.filter((client) => {
        const matchesSearch =
          searchQuery === "" ||
          client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          client.nif.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesState =
          stateFilter === "Todos" || client.status === stateFilter;
        return matchesSearch && matchesState;
      }),
    [clients, searchQuery, stateFilter],
  );

  const visibleDocs = useMemo(
    () => documents.filter((d) => d.type === activeDocTab),
    [documents, activeDocTab],
  );

  // Si el cliente seleccionado queda fuera del filtro activo, seleccionar el primero visible
  useEffect(() => {
    if (
      selectedClient &&
      !filteredClients.find((c) => c.id === selectedClient.id)
    ) {
      setSelectedClient(filteredClients[0] ?? null);
    }
  }, [filteredClients, selectedClient]);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const blob = await usersService.exportExcel({ role: 2 });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clientes.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting clients:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeactivateClient = async (clientId: string) => {
    if (togglingId !== null) return;
    const target = clients.find((c) => c.id === clientId);
    if (!target) return;
    setTogglingId(clientId);
    try {
      if (target.status === "active") {
        // DELETE = soft-delete en el backend (is_active → false)
        await usersService.remove(Number(clientId));
      } else {
        // PATCH para reactivar (DELETE no puede deshacer la desactivación)
        await usersService.patch(Number(clientId), { is_active: true });
      }
      await loadClients(page);
    } catch (err) {
      console.error("Error toggling client status:", err);
    } finally {
      setTogglingId(null);
    }
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setShowClientModal(true);
  };

  const handleCreateClient = () => {
    setEditingClient(null);
    setShowClientModal(true);
  };

  const handleSaveClient = async (clientData: Partial<Client>) => {
    try {
      const apiData = mapClientToAPIUserRequest(clientData);
      if (editingClient) {
        await usersService.updateFormData(
          Number(editingClient.id),
          apiData as APIUserRequest,
        );
      } else {
        await usersService.createFormData({
          ...(apiData as APIUserRequest),
          role: 2,
        });
      }
      await loadClients(page);
    } catch (err) {
      console.error("Error saving client:", err);
    }
    setShowClientModal(false);
    setEditingClient(null);
  };

  return (
    <div className={styles.clientsPage}>
      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Gestión de Clientes</h2>
          </div>
          <div className={styles.headerRight}>
            <button
              className={styles.filterButton}
              onClick={() => {
                setStateFilter("Todos");
                setSearchQuery("");
              }}
            >
              <Icon name="filter" size={16} />
              Limpiar filtros
            </button>
            <button
              className={styles.newClientButton}
              onClick={handleCreateClient}
            >
              <Icon name="plus" size={18} />
              Nuevo Cliente
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Estado:</label>
            <select
              className={styles.select}
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <option>Todos</option>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>

          <div className={styles.searchBox}>
            <span className={styles.searchIcon}>
              <Icon name="search" size={16} />
            </span>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Buscar cliente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableContainer}>
          {isLoading ? (
            <div className={styles.emptyState}>
              <p>Cargando clientes...</p>
            </div>
          ) : filteredClients.length > 0 ? (
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th>CIF / NIF</th>
                  <th>CLIENTE</th>
                  <th>CONTACTO</th>
                  <th>DIRECCIÓN</th>
                  <th>ESTADO</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody className={styles.tableBody}>
                {filteredClients.map((client) => (
                  <tr
                    key={client.id}
                    className={
                      selectedClient?.id === client.id ? styles.selected : ""
                    }
                    onClick={() => {
                      setSelectedClient(client);
                      setActiveDocTab("contrato");
                    }}
                  >
                    <td>
                      <span style={{ fontSize: "12px", color: "#6b7280" }}>
                        {client.nif}
                      </span>
                    </td>
                    <td>
                      <div className={styles.clientInfo}>
                        <div
                          className={styles.clientAvatar}
                          style={{
                            backgroundColor: getAvatarColor(client.name),
                          }}
                        >
                          {getInitials(client.name)}
                        </div>
                        <div className={styles.clientDetails}>
                          <p className={styles.clientName}>{client.name}</p>
                          <p className={styles.clientSubtext}>
                            {client.contactPerson}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className={styles.contactInfo}>
                        <Icon
                          name="phone"
                          size={14}
                          className={styles.contactIcon}
                        />
                        {client.phone}
                      </div>
                    </td>
                    <td>
                      <div className={styles.contactInfo}>
                        <Icon
                          name="mapPin"
                          size={14}
                          className={styles.contactIcon}
                        />
                        {client.address}, {client.city}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${styles[client.status]}`}
                      >
                        <Icon
                          name={
                            client.status === "active"
                              ? "checkCircle"
                              : "xCircle"
                          }
                          size={12}
                        />
                        {client.status === "active" ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.editButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClient(client);
                          }}
                          title="Editar"
                        >
                          <Icon name="edit" size={16} />
                          Editar
                        </button>
                        <button
                          className={styles.deactivateButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDeactivateClient(client.id);
                          }}
                          disabled={togglingId === client.id}
                          title={
                            togglingId === client.id
                              ? "Procesando..."
                              : client.status === "active"
                                ? "Desactivar"
                                : "Activar"
                          }
                        >
                          <Icon name="power" size={16} />
                          {client.status === "active"
                            ? "Desactivar"
                            : "Activar"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.emptyState}>
              <Icon name="users" size={48} />
              <p>No se encontraron clientes</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "8px",
              padding: "12px 16px",
              fontSize: "14px",
              color: "#6b7280",
            }}
          >
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: "4px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
                background: "white",
                cursor: page === 1 ? "not-allowed" : "pointer",
                opacity: page === 1 ? 0.5 : 1,
              }}
            >
              ‹ Anterior
            </button>
            <span>
              Página {page} de {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                padding: "4px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
                background: "white",
                cursor: page === totalPages ? "not-allowed" : "pointer",
                opacity: page === totalPages ? 0.5 : 1,
              }}
            >
              Siguiente ›
            </button>
          </div>
        )}
      </div>

      {/* Sidebar */}
      {selectedClient && (
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <div className={styles.sidebarClient}>
              <div
                className={styles.sidebarAvatar}
                style={{ backgroundColor: getAvatarColor(selectedClient.name) }}
              >
                {getInitials(selectedClient.name)}
              </div>
              <div className={styles.sidebarClientInfo}>
                <h3>{selectedClient.name}</h3>
                <div className={styles.sidebarClientMeta}>
                  <span>{selectedClient.city}</span>
                  <span>•</span>
                  <span>{selectedClient.phone}</span>
                </div>
              </div>
            </div>
            <button
              className={styles.exportButton}
              title={
                isExporting ? "Exportando..." : "Exportar clientes a Excel"
              }
              onClick={() => void handleExport()}
              disabled={isExporting}
            >
              <Icon name="download" size={18} />
            </button>
          </div>

          <div className={styles.sidebarContent}>
            {/* Contact Info */}
            <div className={styles.sidebarSection}>
              <h4>CONTACTO</h4>
              <div className={styles.contactList}>
                <div className={styles.contactItem}>
                  <Icon
                    name="phone"
                    size={16}
                    className={styles.contactItemIcon}
                  />
                  <span>{selectedClient.phone}</span>
                </div>
                <div className={styles.contactItem}>
                  <Icon
                    name="mail"
                    size={16}
                    className={styles.contactItemIcon}
                  />
                  <span>{selectedClient.email}</span>
                </div>
                <div className={styles.contactItem}>
                  <Icon
                    name="mapPin"
                    size={16}
                    className={styles.contactItemIcon}
                  />
                  <span>
                    {selectedClient.address}, {selectedClient.postalCode}{" "}
                    {selectedClient.city}
                  </span>
                </div>
              </div>
            </div>

            {/* Service History */}
            <div className={styles.sidebarSection}>
              <div className={styles.serviceHistoryHeader}>
                <h4>HISTORIAL DE SERVICIOS</h4>
                {!isLoadingServiceHistory && serviceHistory.length > 4 && (
                  <button
                    className={styles.serviceHistoryMoreButton}
                    onClick={openServiceHistoryModal}
                  >
                    Ver más
                  </button>
                )}
              </div>
              <div className={styles.serviceHistory}>
                {isLoadingServiceHistory ? (
                  <p className={styles.documentEmptyMessage}>
                    Cargando historial...
                  </p>
                ) : serviceHistory.length === 0 ? (
                  <p className={styles.documentEmptyMessage}>
                    No hay servicios para este cliente.
                  </p>
                ) : (
                  latestServices.map((service, index) => (
                    <div key={service.id} className={styles.serviceItem}>
                      <div className={styles.serviceIndicator}>
                        <div
                          className={`${styles.serviceIcon} ${styles[service.status]}`}
                        />
                        {index < latestServices.length - 1 && (
                          <div className={styles.serviceLine} />
                        )}
                      </div>
                      <div className={styles.serviceContent}>
                        <p className={styles.serviceTitle}>{service.title}</p>
                        <p className={styles.serviceDescription}>
                          {service.description}
                        </p>
                        <div className={styles.serviceDate}>
                          <Icon name="calendar" size={12} />
                          {service.date} •{" "}
                          {service.status === "completed"
                            ? "Finalizado"
                            : "Programado"}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Documents */}
            <div className={styles.documentsSection}>
              <h4>DOCUMENTACIÓN</h4>
              <div className={styles.documentTabs}>
                <button
                  className={`${styles.documentTab} ${activeDocTab === "contrato" ? styles.active : ""}`}
                  onClick={() => setActiveDocTab("contrato")}
                >
                  Contratos
                </button>
                <button
                  className={`${styles.documentTab} ${activeDocTab === "certificado" ? styles.active : ""}`}
                  onClick={() => setActiveDocTab("certificado")}
                >
                  Certificados
                </button>
                <button
                  className={`${styles.documentTab} ${activeDocTab === "inspeccion" ? styles.active : ""}`}
                  onClick={() => setActiveDocTab("inspeccion")}
                >
                  Inspecciones
                </button>
              </div>
              <div className={styles.documentList}>
                {isLoadingDocs ? (
                  <p className={styles.documentEmptyMessage}>
                    Cargando documentos...
                  </p>
                ) : (
                  visibleDocs.map((doc) => (
                    <div key={doc.id} className={styles.documentItem}>
                      <div className={styles.documentInfo}>
                        <Icon
                          name="document"
                          size={18}
                          className={styles.documentIcon}
                        />
                        <div className={styles.documentDetails}>
                          <p className={styles.documentName}>{doc.name}</p>
                          <p className={styles.documentMeta}>
                            {doc.date} {doc.size ? `• ${doc.size}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className={styles.docItemActions}>
                        {doc.url && (
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.downloadButton}
                            title="Descargar"
                          >
                            <Icon name="download" size={14} />
                          </a>
                        )}
                        <button
                          className={`${styles.deleteDocButton} ${
                            isDeletingDocId === doc.numericId
                              ? styles.deleteDocButtonDeleting
                              : ""
                          }`}
                          title="Eliminar documento"
                          disabled={isDeletingDocId === doc.numericId}
                          onClick={() => {
                            if (!selectedClient) return;
                            setIsDeletingDocId(doc.numericId);
                            usersService
                              .deleteDocument(
                                Number(selectedClient.id),
                                doc.numericId,
                              )
                              .then(() => {
                                setDocuments((prev) =>
                                  prev.filter((d) => d.id !== doc.id),
                                );
                              })
                              .catch((err) =>
                                console.error("Error deleting document:", err),
                              )
                              .finally(() => setIsDeletingDocId(null));
                          }}
                        >
                          <Icon name="xCircle" size={14} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
                {!isLoadingDocs && visibleDocs.length === 0 && (
                  <p className={styles.documentEmptyMessage}>
                    No hay documentos en esta categoría.
                  </p>
                )}
              </div>
              <button
                className={styles.addDocumentButton}
                disabled={isLoadingDocs}
                onClick={() => {
                  setUploadDocType(activeDocTab as Document["type"]);
                  setUploadFileName("");
                  setUploadError(null);
                  setShowUploadPanel((v) => !v);
                }}
              >
                + Subir nuevo documento
              </button>

              {/* Panel de subida inline */}
              {showUploadPanel && (
                <div className={styles.uploadPanel}>
                  <div className={styles.uploadPanelRow}>
                    <label className={styles.uploadPanelLabel}>Tipo</label>
                    <select
                      className={styles.uploadPanelSelect}
                      value={uploadDocType}
                      onChange={(e) =>
                        setUploadDocType(e.target.value as Document["type"])
                      }
                    >
                      <option value="contrato">Contrato</option>
                      <option value="certificado">Certificado</option>
                      <option value="inspeccion">Inspección</option>
                    </select>
                  </div>
                  <div className={styles.uploadPanelRow}>
                    <label className={styles.uploadPanelLabel}>Archivo</label>
                    <button
                      type="button"
                      className={styles.uploadFileButton}
                      onClick={() => uploadInputRef.current?.click()}
                    >
                      {uploadFileName || "Seleccionar archivo…"}
                    </button>
                    <input
                      ref={uploadInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.png,.jpeg"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setUploadFileName(file.name);
                      }}
                    />
                  </div>
                  {uploadError && (
                    <p className={styles.uploadErrorMessage}>{uploadError}</p>
                  )}
                  <div className={styles.uploadPanelActions}>
                    <button
                      type="button"
                      className={styles.uploadConfirmButton}
                      disabled={!uploadFileName || isUploading}
                      onClick={() => {
                        const file = uploadInputRef.current?.files?.[0];
                        if (!file || !selectedClient) return;
                        // Captura clientId de forma estable antes de la cadena async
                        // para evitar closures rancias si el usuario navega durante la subida
                        const clientId = selectedClient.id;
                        setIsUploading(true);
                        setUploadError(null);
                        // Bandera local: uploadError del closure sería el valor
                        // anterior al .catch(), siempre null en el .finally()
                        let uploadFailed = false;
                        usersService
                          .uploadDocument(Number(clientId), file, uploadDocType)
                          .then(() => void loadDocuments(clientId))
                          .catch((err) => {
                            uploadFailed = true;
                            console.error("Error uploading document:", err);
                            setUploadError(
                              "Error al subir el documento. Inténtalo de nuevo.",
                            );
                          })
                          .finally(() => {
                            setIsUploading(false);
                            if (!uploadFailed) {
                              setShowUploadPanel(false);
                              setUploadFileName("");
                              if (uploadInputRef.current)
                                uploadInputRef.current.value = "";
                            }
                          });
                      }}
                    >
                      {isUploading ? "Subiendo..." : "Confirmar"}
                    </button>
                    <button
                      type="button"
                      className={styles.uploadCancelButton}
                      onClick={() => {
                        setShowUploadPanel(false);
                        setUploadFileName("");
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>
      )}

      {/* Client Modal */}
      <ClientModal
        isOpen={showClientModal}
        client={editingClient}
        provinces={provinces}
        cities={allCities}
        onClose={() => {
          setShowClientModal(false);
          setEditingClient(null);
        }}
        onSave={(clientData) => void handleSaveClient(clientData)}
      />

      {showServiceHistoryModal && (
        <div
          className={styles.historyModalOverlay}
          onClick={() => setShowServiceHistoryModal(false)}
        >
          <div
            className={styles.historyModal}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.historyModalHeader}>
              <h3 className={styles.historyModalTitle}>Historial completo</h3>
              <button
                className={styles.historyModalClose}
                onClick={() => setShowServiceHistoryModal(false)}
              >
                <Icon name="xCircle" size={18} />
              </button>
            </div>

            <div
              className={styles.historyModalBody}
              onScroll={handleServiceHistoryModalScroll}
            >
              {visibleServiceHistoryInModal.map((service, index) => (
                <div key={`full-${service.id}`} className={styles.serviceItem}>
                  <div className={styles.serviceIndicator}>
                    <div
                      className={`${styles.serviceIcon} ${styles[service.status]}`}
                    />
                    {index < visibleServiceHistoryInModal.length - 1 && (
                      <div className={styles.serviceLine} />
                    )}
                  </div>
                  <div className={styles.serviceContent}>
                    <p className={styles.serviceTitle}>{service.title}</p>
                    <p className={styles.serviceDescription}>
                      {service.description}
                    </p>
                    <div className={styles.serviceDate}>
                      <Icon name="calendar" size={12} />
                      {service.date} •{" "}
                      {service.status === "completed"
                        ? "Finalizado"
                        : "Programado"}
                    </div>
                  </div>
                </div>
              ))}
              {historyModalVisibleCount < serviceHistory.length && (
                <p className={styles.historyModalHint}>
                  Desplázate para cargar más
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientsPage;
